import React from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getFoodCourtItemById, deleteFoodCourtItem } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import { 
  IndividualViewPageLayout, 
  SidebarSection, 
  ActionButtons, 
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
  ActionButton
} from '../../components/layout';
import { 
  Edit, 
  Trash2, 
  Tag, 
  Phone, 
  Clock, 
  Utensils,
  Coffee,
  Star,
  AlertCircle
} from 'lucide-react';
import { MenuViewerButton } from './MenuViewer';
import { hasFoodCourtMenu } from './menuTypes';

export default function ViewFoodCourtPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { itemId, slug } = useParams<{ itemId: string; slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const foodCourtListPath = locationSectionPath(locSlug, 'food-court');
  const deleteFoodCourtItemFn = useAction(deleteFoodCourtItem);

  const { data: item, isLoading, error } = useQuery(getFoodCourtItemById, { 
    itemId: itemId || '' 
  });

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteFoodCourtItemFn({ itemId: itemId! });
        // Navigate back to food court page
        navigate(foodCourtListPath);
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const handleGoBack = () => {
    navigate(foodCourtListPath);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading item...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Item Not Found</h2>
            <p className="text-gray-600">The item you're looking for doesn't exist or you don't have permission to view it.</p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = !!user && ((item as any).authorId === user.id || isAdministrator(user));

  const getCategoryColor = (category: string) => {
    const colors = {
      'FOOD': 'bg-orange-100 text-orange-800',
      'BEVERAGE': 'bg-blue-100 text-blue-800',
      'DESSERT': 'bg-pink-100 text-pink-800',
      'SNACK': 'bg-yellow-100 text-yellow-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      'AVAILABLE': 'bg-green-100 text-green-800',
      'LIMITED': 'bg-yellow-100 text-yellow-800',
      'OUT_OF_STOCK': 'bg-red-100 text-red-800'
    };
    return colors[availability as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'FOOD': return <Utensils className="w-5 h-5" />;
      case 'BEVERAGE': return <Coffee className="w-5 h-5" />;
      case 'DESSERT': return <Star className="w-5 h-5" />;
      case 'SNACK': return <Tag className="w-5 h-5" />;
      default: return <Utensils className="w-5 h-5" />;
    }
  };

  const currentPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const breadcrumbItems = [
    {
      label: 'Community',
      dropdownItems: [
        { label: 'Buy and Sell', href: locationSectionPath(locSlug, 'buy-sell') },
        { label: 'Food Court', href: foodCourtListPath },
        { label: 'Lost and Found', href: locationSectionPath(locSlug, 'lost-found') },
        { label: 'Services', href: locationSectionPath(locSlug, 'services') },
      ]
    },
    {
      label: 'Food Court',
      isClickable: true,
      href: foodCourtListPath
    }
  ];

  const sidebarContent = (
    <>
      {isOwner && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={locationSectionPath(locSlug, 'food-court', itemId!, 'edit')}
              icon={Edit}
            >
              Edit Item
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              icon={Trash2}
            >
              Delete Item
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Item Details">
        <div className="space-y-4">
          <DetailField label="Category" value={(item as any).metadata?.category || 'N/A'} />
          {(item as any).metadata?.cuisine && (
            <DetailField label="Cuisine" value={(item as any).metadata.cuisine} />
          )}
          {(item as any).metadata?.availability && (
            <DetailField label="Availability" value={(item as any).metadata.availability.replace('_', ' ')} />
          )}
        </div>
      </SidebarSection>

      <SidebarSection title="Post Information">
        <div className="space-y-4">
          <DetailField 
            label="Posted by" 
            value={(item as any).author?.name || (item as any).author?.email || 'N/A'} 
          />
          <DetailField 
            label="Posted on" 
            value={new Date((item as any).createdAt).toLocaleDateString()} 
          />
        </div>
      </SidebarSection>

      {!isOwner && (
        <SidebarSection>
          <div className="space-y-3">
            <button className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium mb-3">
              <Phone className="w-4 h-4" />
              Contact Seller
            </button>
            <ActionButton icon={Star}>
              Save Item
            </ActionButton>
          </div>
        </SidebarSection>
      )}
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
                {/* Item Header */}
                <div className="mb-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-sm font-medium rounded ${getCategoryColor((item as any).metadata?.category)}`}>
                        <div className="flex items-center gap-2">
                          {getCategoryIcon((item as any).metadata?.category)}
                          {(item as any).metadata?.category}
                        </div>
                      </span>
                      {(item as any).metadata?.availability && (
                        <span className={`px-3 py-1 text-sm font-medium rounded ${getAvailabilityColor((item as any).metadata.availability)}`}>
                          {(item as any).metadata.availability.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {(item as any).title}
                  </h1>
                  
                  {(item as any).metadata?.cuisine && (
                    <div className="flex items-center gap-2 text-gray-600 mb-4">
                      <Utensils className="w-4 h-4" />
                      <span className="text-lg">{(item as any).metadata.cuisine} Cuisine</span>
                    </div>
                  )}
                  
                  {(item as any).metadata?.price && (
                    <div className="text-lg text-gray-600 mb-2">
                      {(item as any).metadata.price}
                    </div>
                  )}

                  {hasFoodCourtMenu((item as any).metadata) && (
                    <div className="mb-4">
                      <MenuViewerButton
                        metadata={(item as any).metadata}
                        itemTitle={(item as any).title}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/15 font-medium"
                      />
                    </div>
                  )}
                </div>

                {/* Item Details */}
                <div className="mb-8">
                  <div className="prose prose-lg max-w-none mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {(item as any).description}
                    </p>
                  </div>

                  {/* Dietary Information */}
                  {(item as any).metadata?.dietaryInfo && (item as any).metadata.dietaryInfo.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Dietary Information</h3>
                      <div className="flex flex-wrap gap-2">
                        {(item as any).metadata.dietaryInfo.map((diet: string, index: number) => (
                          <span key={index} className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">
                            {diet}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {(item as any).metadata?.contactInfo && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4" />
                        <span>{(item as any).metadata.contactInfo}</span>
                      </div>
                    </div>
                  )}
                </div>
    </IndividualViewPageLayout>
  );
}



